import { Zone, ZoneType } from "@fairfleet/geotab";
import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import { useSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ConfirmDialog from "./components/ConfirmDialog";
import ZoneForm from "./components/ZoneForm";
import ZoneList from "./components/ZoneList";
import ZonesMap, { MapFocus } from "./components/ZonesMap";
import ZoneTypeDialog from "./components/ZoneTypeDialog";
import ZoneTypeList from "./components/ZoneTypeList";
import { useZoneEditor } from "./hooks/useZoneEditor";
import { FALLBACK_ZONE_COLOR } from "./utils/zoneColor";
import { countZonesByType, emptyZoneDraft, zoneToDraft } from "./utils/zoneDraft";
import { boundsOf, geotabPointsToLngLat, LngLat } from "./utils/zoneGeometry";
import { normalizeZoneTypes, sortZoneTypes, withBuiltInZoneTypes, zoneTypeLabel } from "./utils/zoneTypes";
import { useAddInState } from "@/components/AddInStateProvider";
import { useGroups } from "@/hooks/groups/useGroups";
import { useAllZones } from "@/hooks/zone/useAllZones";
import { useRemoveZone, useRemoveZoneType, useSaveZone, useSaveZoneType } from "@/hooks/zone/useZoneMutations";
import { useZoneTypes } from "@/hooks/zone/useZoneTypes";

const PANEL_WIDTH = 360;
const PANEL_GAP = 12;
const COMPANY_GROUP_ID = "GroupCompanyId";
/** Half the edge of the box flown to around a single address, in degrees (≈ 1 km). */
const ADDRESS_FOCUS_DEG = 0.005;

type Tab = "zones" | "types";
type TypeDialogState = { type?: ZoneType } | null;

const errorMessage = (error: unknown) => (error instanceof Error ? error.message : String(error));

export default function ZonesPage() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { groups: filterGroups } = useAddInState();
  const zonesQuery = useAllZones();
  const typesQuery = useZoneTypes();
  const { data: groups = [] } = useGroups();
  const saveZone = useSaveZone();
  const removeZone = useRemoveZone();
  const saveZoneType = useSaveZoneType();
  const removeZoneType = useRemoveZoneType();
  const editor = useZoneEditor();

  const zones = useMemo(() => zonesQuery.data ?? [], [zonesQuery.data]);
  const types = useMemo(() => sortZoneTypes(withBuiltInZoneTypes(normalizeZoneTypes(typesQuery.data ?? [])), t), [typesQuery.data, t]);
  const counts = useMemo(() => countZonesByType(zones), [zones]);

  const [tab, setTab] = useState<Tab>("zones");
  const [collapsed, setCollapsed] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [selectedZoneId, setSelectedZoneId] = useState<string>();
  const [editingZone, setEditingZone] = useState<Zone | null>(null);
  const [confirmZoneDelete, setConfirmZoneDelete] = useState(false);
  const [typeDialog, setTypeDialog] = useState<TypeDialogState>(null);
  const [typeToRemove, setTypeToRemove] = useState<ZoneType | null>(null);
  const [focus, setFocus] = useState<MapFocus | null>(null);
  const [initialFitDone, setInitialFitDone] = useState(false);

  const flyTo = (bounds: [LngLat, LngLat] | null) => bounds && setFocus(f => ({ bounds, key: (f?.key ?? 0) + 1 }));
  const flyToPoint = ([lng, lat]: LngLat) =>
    flyTo([
      [lng - ADDRESS_FOCUS_DEG, lat - ADDRESS_FOCUS_DEG],
      [lng + ADDRESS_FOCUS_DEG, lat + ADDRESS_FOCUS_DEG],
    ]);

  if (!initialFitDone && zonesQuery.isSuccess) {
    setInitialFitDone(true);
    flyTo(boundsOf(zones.flatMap(zone => geotabPointsToLngLat(zone.points))));
  }

  const editing = editor.draft !== null;

  useEffect(() => {
    if (!editor.drawing) return;
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && editor.resetPoints();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editor]);

  const openZone = (id: string) => {
    const zone = zones.find(z => z.id === id);
    if (!zone) return;
    setSelectedZoneId(id);
    setEditingZone(zone);
    setCollapsed(false);
    editor.start(zoneToDraft(zone), "draw");
    flyTo(boundsOf(geotabPointsToLngLat(zone.points)));
  };

  const createZone = () => {
    const groupIds = filterGroups?.length ? filterGroups.map(group => group.id) : [COMPANY_GROUP_ID];
    setEditingZone(null);
    setSelectedZoneId(undefined);
    setCollapsed(false);
    editor.start(emptyZoneDraft(groupIds, FALLBACK_ZONE_COLOR), "address");
  };

  const closeEditor = () => {
    editor.stop();
    setEditingZone(null);
  };

  const handleSave = () => {
    if (!editor.draft) return;
    saveZone.mutate(
      { draft: editor.draft, original: editingZone ?? undefined },
      {
        onSuccess: id => {
          enqueueSnackbar(t("zonesPage.saved"), { variant: "success" });
          setSelectedZoneId(id);
          closeEditor();
        },
        onError: error => enqueueSnackbar(t("zonesPage.saveFailed", { message: errorMessage(error) }), { variant: "error" }),
      },
    );
  };

  const handleDeleteZone = () => {
    if (!editingZone) return;
    removeZone.mutate(editingZone, {
      onSuccess: () => {
        enqueueSnackbar(t("zonesPage.deleted"), { variant: "success" });
        setConfirmZoneDelete(false);
        setSelectedZoneId(undefined);
        closeEditor();
      },
      onError: error => {
        setConfirmZoneDelete(false);
        enqueueSnackbar(t("zonesPage.deleteFailed", { message: errorMessage(error) }), { variant: "error" });
      },
    });
  };

  const handleSaveType = (values: { name: string; comment: string; fillColor: ZoneType["fillColor"] }) => {
    saveZoneType.mutate(
      { ...values, original: typeDialog?.type },
      {
        onSuccess: () => {
          enqueueSnackbar(t("zonesPage.types.saved"), { variant: "success" });
          setTypeDialog(null);
        },
        onError: error => enqueueSnackbar(t("zonesPage.saveFailed", { message: errorMessage(error) }), { variant: "error" }),
      },
    );
  };

  const handleRemoveType = () => {
    if (!typeToRemove) return;
    removeZoneType.mutate(typeToRemove, {
      onSuccess: () => {
        enqueueSnackbar(t("zonesPage.types.deleted"), { variant: "success" });
        setTypeToRemove(null);
      },
      onError: error => {
        setTypeToRemove(null);
        enqueueSnackbar(t("zonesPage.deleteFailed", { message: errorMessage(error) }), { variant: "error" });
      },
    });
  };

  return (
    <Box sx={{ width: "100%", height: "100%", position: "relative", overflow: "hidden" }}>
      <ZonesMap
        zones={zones}
        selectedZoneId={selectedZoneId}
        editingZoneId={editingZone?.id}
        editing={editing}
        draftPoints={editor.draft?.points ?? []}
        draftColor={editor.draft?.fillColor}
        drawing={editor.drawing}
        addressPin={editor.mode === "address" ? editor.addressCenter : null}
        focus={focus}
        onZoneClick={openZone}
        onMapClick={editor.addPoint}
        onFinishDrawing={editor.finishDrawing}
        onVertexMove={editor.moveVertex}
        onVertexRemove={editor.removeVertex}
      />

      {collapsed ? (
        <Paper elevation={4} sx={{ position: "absolute", top: PANEL_GAP, left: PANEL_GAP }}>
          <Tooltip title={t("zonesPage.expandPanel")}>
            <IconButton onClick={() => setCollapsed(false)}>
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>
        </Paper>
      ) : (
        <Paper
          elevation={4}
          sx={{
            position: "absolute",
            top: PANEL_GAP,
            left: PANEL_GAP,
            bottom: PANEL_GAP,
            width: PANEL_WIDTH,
            maxWidth: `calc(100% - ${PANEL_GAP * 2}px)`,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {editing ? (
            <ZoneForm
              key={editingZone?.id ?? "new"}
              editor={editor}
              types={types}
              groups={groups}
              isNew={editingZone === null}
              saving={saveZone.isPending}
              onSave={handleSave}
              onCancel={closeEditor}
              onDelete={() => setConfirmZoneDelete(true)}
              onFlyTo={flyToPoint}
            />
          ) : (
            <>
              <Box sx={{ display: "flex", alignItems: "center", borderBottom: 1, borderColor: "divider" }}>
                <Tabs value={tab} onChange={(_, value: Tab) => setTab(value)} sx={{ flex: 1 }}>
                  <Tab value="zones" label={t("zonesPage.tabs.zones")} />
                  <Tab value="types" label={t("zonesPage.tabs.types")} />
                </Tabs>
                <Tooltip title={t("zonesPage.collapsePanel")}>
                  <IconButton size="small" onClick={() => setCollapsed(true)}>
                    <ChevronLeftIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              {tab === "zones" ? (
                <>
                  <Box sx={{ px: 1, pt: 1 }}>
                    <Button fullWidth variant="contained" startIcon={<AddIcon />} onClick={createZone}>
                      {t("zonesPage.newZone")}
                    </Button>
                  </Box>
                  <ZoneList
                    zones={zones}
                    types={types}
                    loading={zonesQuery.isLoading}
                    truncated={zonesQuery.isTruncated}
                    selectedZoneId={selectedZoneId}
                    typeFilter={typeFilter}
                    onTypeFilterChange={setTypeFilter}
                    onSelect={openZone}
                  />
                </>
              ) : (
                <ZoneTypeList
                  types={types}
                  counts={counts}
                  onCreate={() => setTypeDialog({})}
                  onEdit={type => setTypeDialog({ type })}
                  onRemove={setTypeToRemove}
                  onShowZones={typeId => {
                    setTypeFilter(typeId);
                    setTab("zones");
                  }}
                />
              )}
            </>
          )}
        </Paper>
      )}

      {typeDialog && (
        <ZoneTypeDialog
          key={typeDialog.type?.id ?? "new"}
          type={typeDialog.type}
          busy={saveZoneType.isPending}
          onSave={handleSaveType}
          onClose={() => setTypeDialog(null)}
        />
      )}
      <ConfirmDialog
        open={confirmZoneDelete}
        title={t("zonesPage.deleteZoneTitle")}
        text={t("zonesPage.deleteZoneText", { name: editingZone?.name ?? "" })}
        busy={removeZone.isPending}
        onConfirm={handleDeleteZone}
        onClose={() => setConfirmZoneDelete(false)}
      />
      <ConfirmDialog
        open={typeToRemove !== null}
        title={t("zonesPage.types.deleteTitle")}
        text={t("zonesPage.types.deleteText", { name: typeToRemove ? zoneTypeLabel(typeToRemove, t) : "" })}
        busy={removeZoneType.isPending}
        onConfirm={handleRemoveType}
        onClose={() => setTypeToRemove(null)}
      />
    </Box>
  );
}
